import axios from 'axios';
import { API_BASE_URL } from '../config/api';
let pendingRefresh = null;
let pendingLogout = null;
let pendingLogin = null;
let generation = 0;
const notify = () => window.dispatchEvent(new Event('auth-session-changed'));
const withSessionLock = operation => typeof navigator !== 'undefined' && navigator.locks?.request
  ? navigator.locks.request('auth-session-refresh', operation)
  : operation();
export function setAccessToken(token) {
  if (token) {
    localStorage.setItem('accessToken', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    delete axios.defaults.headers.common.Authorization;
  }
  notify();
}
export function refreshAccessToken(failedToken) {
  if (pendingLogout) return Promise.reject(new Error('Signed out'));
  const current = localStorage.getItem('accessToken');
  if (!current && failedToken) return Promise.reject(new Error('Signed out'));
  // A late 401 for the previous token must not rotate the cookie again.
  if (current && current !== failedToken) return Promise.resolve(current);
  if (!pendingRefresh) {
    const started = generation;
    const rotate = () => {
      // Another tab may have refreshed or signed out while this tab waited.
      const latest = localStorage.getItem('accessToken');
      if (pendingLogout || started !== generation || (!latest && failedToken)) {
        return Promise.reject(new Error('Signed out'));
      }
      if (latest && latest !== failedToken) {
        setAccessToken(latest);
        return Promise.resolve(latest);
      }
      return axios.post(`${API_BASE_URL}/api/auth/refresh`, {}, { withCredentials: true })
        .then(response => {
          const token = response.data?.accessToken;
          if (!token || started !== generation) throw new Error('Authentication failed');
          setAccessToken(token);
          return token;
        }).catch(error => {
          if (error.response?.status === 401) {
            if (started === generation) setAccessToken(null);
            throw error;
          }
          if (started === generation) window.dispatchEvent(new Event('auth-session-retryable'));
          throw Object.assign(new Error('Session renewal unavailable. Please try again.'), { retryable: true });
        });
    };
    const operation = withSessionLock(rotate);
    pendingRefresh = operation.finally(() => { pendingRefresh = null; });
  }
  return pendingRefresh;
}
export function loginSession(username, password) {
  if (pendingLogin) return pendingLogin;
  const operation = (async () => {
    if (pendingLogout) await pendingLogout.catch(() => {});
    const started = generation;
    return withSessionLock(async () => {
      if (pendingLogout || started !== generation) throw new Error('Authentication superseded');
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, { username, password }, { timeout: 10000, withCredentials: true });
      if (started !== generation) throw new Error('Authentication superseded');
      // Publish login state before releasing the lock to another tab's refresh/logout.
      if (response.data.success) {
        const user = { username, role: response.data.user?.role || 'user' };
        localStorage.setItem('user', JSON.stringify(user));
        setAccessToken(response.data.accessToken);
      }
      return response;
    });
  })();
  const wrapped = operation.finally(() => { if (pendingLogin === wrapped) pendingLogin = null; });
  pendingLogin = wrapped;
  return wrapped;
}
export function logoutSession() {
  if (pendingLogout) return pendingLogout;
  generation += 1;
  // Settle local requests before taking the lock to avoid waiting on our own refresh.
  // Hold the shared lock through revocation and local cleanup after Set-Cookie.
  pendingLogout = Promise.allSettled([pendingRefresh, pendingLogin]).then(() =>
    withSessionLock(async () => {
      const response = await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, { withCredentials: true });
      if (response.status !== 204) throw new Error('Logout failed');
      setAccessToken(null);
      return response;
    })
  ).finally(() => { pendingLogout = null; });
  return pendingLogout;
}
export function installAxiosRefresh() {
  const interceptor = axios.interceptors.response.use(response => response, async error => {
    const request = error.config;
    const url = request?.url || '';
    const isApi = url.startsWith('/api/') || (API_BASE_URL && url.startsWith(`${API_BASE_URL}/api/`));
    if (error.response?.status !== 401 || !request || request._retry || !isApi || /\/api\/auth(?:\/|$)/.test(url)) throw error;
    request._retry = true;
    const authorization = request.headers?.Authorization || request.headers?.authorization || '';
    const token = await refreshAccessToken(authorization.replace(/^Bearer /, ''));
    request.headers = { ...request.headers, Authorization: `Bearer ${token}` };
    return axios(request);
  });
  return () => axios.interceptors.response.eject(interceptor);
}
